import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Upload, ArrowLeft, FileJson, Loader2 } from "lucide-react";
import { toast } from "sonner";

const readFileAsJson = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                resolve(JSON.parse(reader.result));
            } catch (e) {
                reject(new Error("Invalid JSON"));
            }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });

export default function ChatImportPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { call: importStepChat } = useBluelagoon({ method: "importStepChat" });
    const [file, setFile] = useState(null);
    const [targetCompanyId, setTargetCompanyId] = useState("");
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const onPick = (e) => {
        const f = e.target.files?.[0] ?? null;
        setFile(f);
    };

    const onSubmit = useCallback(async () => {
        if (!file || busy) return;
        setBusy(true);
        try {
            const payload = await readFileAsJson(file);
            const res = await importStepChat({
                payload,
                targetCompanyId: targetCompanyId.trim() || undefined,
            });
            const data = res?.data?.data ?? res?.data;
            if (!data?.newStepChatId) {
                toast.error(res?.data?.error || t("adminChats.import.failed"));
                return;
            }
            toast.success(
                t("adminChats.import.success", {
                    count: data.summary?.messages ?? 0,
                })
            );
            navigate(`/admin/chats/${data.newStepChatId}`);
        } catch (e) {
            toast.error(e.message || t("adminChats.import.failed"));
        } finally {
            setBusy(false);
        }
    }, [file, targetCompanyId, busy, importStepChat, navigate, t]);

    return (
        <div className="p-4 max-w-2xl mx-auto">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3">
                <ArrowLeft className="w-4 h-4 mr-1" /> {t("common.back", "Back")}
            </Button>
            <h1 className="text-xl font-semibold mb-1">{t("adminChats.import.title")}</h1>
            <p className="text-sm text-gray-600 mb-4">{t("adminChats.import.description")}</p>

            <div className="border-2 border-dashed rounded p-6 text-center bg-gray-50">
                <FileJson className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <input ref={inputRef} type="file" accept=".json,application/json" onChange={onPick} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                    {t("adminChats.import.pickFile")}
                </Button>
                {file && <div className="text-xs text-gray-700 mt-2">{file.name}</div>}
            </div>

            <label className="block text-sm font-medium mt-4 mb-1">
                {t("adminChats.import.targetCompany")}
            </label>
            <input
                type="text"
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value)}
                placeholder={t("adminChats.import.targetCompanyPlaceholder")}
                className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">{t("adminChats.import.targetCompanyHint")}</p>

            <Button onClick={onSubmit} disabled={!file || busy} className="mt-4">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {t("adminChats.import.submit")}
            </Button>
        </div>
    );
}
